<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractServiceEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    service: {
      type: Object,
      required: false,
    },
    fee: {
      type: Object,
      required: false,
    },
    mode: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getConfiguration = (...args) => {
    switch (props.mode) {
      case 'addService':
        return {
          isEditable: true,
          isDeletable: false,
          isServiceReadonly: false,
        };

      case 'editService':
        return {
          isEditable: !props.service.locked,
          isDeletable: !props.service.locked,
          isServiceReadonly: props.service.locked,
        };

      case 'addServiceFee':
        return {
          isEditable: true,
          isDeletable: true,
          isServiceReadonly: true,
          isServiceFeeReadonly: false,
        };

      case 'editServiceFee':
        return {
          isEditable: !props.fee.locked,
          isDeletable: !props.fee.locked,
          isServiceReadonly: true,
          isServiceFeeReadonly: props.fee.locked,
        };
    }
  };

  const formId = ref(null);
  const labelAlign = ref('right');
  const isEditable = ref(null);
  const isDeletable = ref(null);
  const isServiceReadonly = ref(null);
  const isServiceFeeReadonly = ref(null);
  const messages = ref({
    addService: {
      title: $fn.value.pgettext('Contract', 'Add Service'),
    },
    editService: {
      title: $fn.value.pgettext('Contract', 'Edit Service'),
    },
    addServiceFee: {
      title: $fn.value.pgettext('Contract', 'Add Service Fee'),
    },
    editServiceFee: {
      title: $fn.value.pgettext('Contract', 'Edit Service Fee'),
    },
  });
  const item = ref(null);
  const feeItem = ref(null);
  const currencies = ref(null);
  const services = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'lg',
  }));
  const b_7307096920626688_multiple = computed(() => false);

  const $data = computed(() => ({
    formId: formId.value,
    labelAlign: labelAlign.value,
    isEditable: isEditable.value,
    isDeletable: isDeletable.value,
    isServiceReadonly: isServiceReadonly.value,
    isServiceFeeReadonly: isServiceFeeReadonly.value,
    messages: messages.value,
    item: item.value,
    feeItem: feeItem.value,
    currencies: currencies.value,
    services: services.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_7307096920626688_multiple: b_7307096920626688_multiple.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_isEditable = async () => {
    return getConfiguration().isEditable;
  };
  const $$load_isDeletable = async () => {
    return getConfiguration().isDeletable;
  };
  const $$load_isServiceReadonly = async () => {
    return getConfiguration().isServiceReadonly;
  };
  const $$load_isServiceFeeReadonly = async () => {
    return getConfiguration().isServiceFeeReadonly;
  };
  const $$load_item = async () => {
    return (
      props.service ||
      $fn.value.schemaDefaults('CreateContractService', {
        valid_from: props.contract.lifecycle.valid_from, // $fn.today(),
        contract_id: props.contract.id,
        service: null,
        service_id: null,
        name: null,
      })
    );
  };
  const $$load_feeItem = async () => {
    return (
      props.fee ||
      $fn.value.schemaDefaults('CreateContractServiceFee', {
        unit: _.get(item.value, 'service.unit', null),
        contract_id: props.contract.id,
        service_id: _.get(item.value, 'id', null),
      })
    );
  };
  const $$load_currencies = async () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_services = async () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: {
          query: '*',
          top: 50,
          skip: 0,
          product_types: 'service',
          product_invoice_groups: 'all',
        },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };
  const $$load_invoiceGroups = async () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'isEditable' },
      { name: 'isDeletable' },
      { name: 'isServiceReadonly' },
      { name: 'isServiceFeeReadonly' },
      { name: 'messages' },
      { name: 'item' },
      { name: 'feeItem', dependsOn: ['item'] },
      { name: 'currencies' },
      { name: 'services' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_7307096920626688_multiple', nodeId: '7307096920626688' },
    ],
    loaders: {
      formId: $$load_formId,
      isEditable: $$load_isEditable,
      isDeletable: $$load_isDeletable,
      isServiceReadonly: $$load_isServiceReadonly,
      isServiceFeeReadonly: $$load_isServiceFeeReadonly,
      item: $$load_item,
      feeItem: $$load_feeItem,
      currencies: $$load_currencies,
      services: $$load_services,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      formId,
      labelAlign,
      isEditable,
      isDeletable,
      isServiceReadonly,
      isServiceFeeReadonly,
      messages,
      item,
      feeItem,
      currencies,
      services,
      invoiceGroups,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_7307096920626688_multiple,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const currentObjectType = computed(() => {
    return props.mode.indexOf('Fee') > -1
      ? 'contract_service_fee'
      : 'contract_service';
  });
  const displayFee = computed(() => {
    return props.mode !== 'editService';
  });
  const displayFeeDates = computed(() => {
    return displayFee.value && props.mode !== 'addService';
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return { title: messages.value[props.mode].title };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.service,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('service');
    }
  );
  watch(
    () => props.fee,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fee');
    }
  );
  watch(
    () => props.mode,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('mode');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ljuuqz689_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_vczu5oa69_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_fm43l1nic_getActionArgs = async ($event) => {
    return {
      objectType: currentObjectType.value,
      op: 'upsert',
      data:
        props.mode === 'addService'
          ? _.extend({}, item.value, {
              fees: [
                _.extend(feeItem.value, {
                  valid_from: item.value.valid_from,
                  valid_to: item.value.valid_to,
                }),
              ],
            })
          : currentObjectType.value == 'contract_service'
            ? item.value
            : feeItem.value,
      options: {
        parameters: {
          rev: props.contract.revision_number,
        },
      },
    };
  };
  const act_3054425420812800_setData_uc9gbew07_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.name',
      value: $event.data.option.name,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3054425420812800_setData_5ofbyuff9_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: $event.data.option.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3054425420812800_setData_p567odxhu_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'feeItem.unit',
      value: $event.data.option.unit,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1740443260293186_closeModal_1pe252t06_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1740443260293186_request_wl1jh7omj_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_contract_service_fee',
      data: {
        id: feeItem.value.id,
        etag: feeItem.value.etag,
        locked: true,
      },
    };
  };
  const act_2140198529698299_closeModal_nv032x67k_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2140198529698299_request_mhch6pvxq_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_contract_service',
      data: {
        id: item.value.id,
        etag: item.value.etag,
        locked: true,
      },
    };
  };
  const act_2434514588044777_closeModal_icubhjh1i_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2434514588044777_request_fl9jk0g7b_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_contract_service',
      data: item.value,
    };
  };
  const act_2167826738365259_closeModal_of3hfm9fa_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_2167826738365259_request_kq4z07xo0_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_contract_service_fee',
      data: feeItem.value,
    };
  };

  const act_7315092382398562_closeModal_ljuuqz689 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ljuuqz689_getActionArgs,
    });
  const inst_evh_7315092382398562_close_lk3ej7jf3 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ljuuqz689,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_lk3ej7jf3],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_fxhe83exo =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_c1opecdov =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_fxhe83exo,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_c1opecdov],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_mmv9wblg9 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_7lopvbvbf =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_mmv9wblg9,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_7lopvbvbf],
    event: 'close',
  });
  const act_1040167445267876_closeModal_vczu5oa69 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_vczu5oa69_getActionArgs,
    });
  const inst_evh_1040167445267876_success_xdb0kjfut =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_vczu5oa69,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_fm43l1nic = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_fm43l1nic_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_xdb0kjfut],
  });
  const inst_evh_1040167445267876_submit_ffe512i56 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_fm43l1nic,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_ffe512i56],
    event: 'submit',
  });
  const act_3054425420812800_setData_uc9gbew07 = new actionTypes.SetDataAction({
    actionArgs: act_3054425420812800_setData_uc9gbew07_getActionArgs,
  });
  const inst_evh_3054425420812800_optionSelected_nvh3fup7q =
    new actionTypes.EventHandlerImpl({
      action: act_3054425420812800_setData_uc9gbew07,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_3054425420812800_setData_5ofbyuff9 = new actionTypes.SetDataAction({
    actionArgs: act_3054425420812800_setData_5ofbyuff9_getActionArgs,
  });
  const inst_evh_3054425420812800_optionSelected_8gof7ik2d =
    new actionTypes.EventHandlerImpl({
      action: act_3054425420812800_setData_5ofbyuff9,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_3054425420812800_setData_p567odxhu = new actionTypes.SetDataAction({
    actionArgs: act_3054425420812800_setData_p567odxhu_getActionArgs,
  });
  const inst_evh_3054425420812800_optionSelected_ca24tmnd2 =
    new actionTypes.EventHandlerImpl({
      action: act_3054425420812800_setData_p567odxhu,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_3054425420812800_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_3054425420812800_optionSelected_nvh3fup7q,
        inst_evh_3054425420812800_optionSelected_8gof7ik2d,
        inst_evh_3054425420812800_optionSelected_ca24tmnd2,
      ],
      event: 'optionSelected',
    });
  const act_1740443260293186_closeModal_1pe252t06 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1740443260293186_closeModal_1pe252t06_getActionArgs,
    });
  const inst_evh_1740443260293186_success_xecrkyvi5 =
    new actionTypes.EventHandlerImpl({
      action: act_1740443260293186_closeModal_1pe252t06,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1740443260293186_request_wl1jh7omj = new actionTypes.RequestAction({
    actionArgs: act_1740443260293186_request_wl1jh7omj_getActionArgs,
    displayName: 'lockServiceFee',
    events: [inst_evh_1740443260293186_success_xecrkyvi5],
  });
  const inst_evh_1740443260293186_clickPreventStop_dd8idry6u =
    new actionTypes.EventHandlerImpl({
      action: act_1740443260293186_request_wl1jh7omj,
      event: 'click.prevent.stop',
      displayName: 'lockServiceFee',
    });
  const inst_evh_1740443260293186_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1740443260293186_clickPreventStop_dd8idry6u],
      event: 'click.prevent.stop',
    });
  const act_2140198529698299_closeModal_nv032x67k =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_nv032x67k_getActionArgs,
    });
  const inst_evh_2140198529698299_success_gji4f9yp7 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_nv032x67k,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2140198529698299_request_mhch6pvxq = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_mhch6pvxq_getActionArgs,
    displayName: 'lockService',
    events: [inst_evh_2140198529698299_success_gji4f9yp7],
  });
  const inst_evh_2140198529698299_clickPreventStop_5f8hjcagm =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_mhch6pvxq,
      event: 'click.prevent.stop',
      displayName: 'lockService',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_5f8hjcagm],
      event: 'click.prevent.stop',
    });
  const act_2434514588044777_closeModal_icubhjh1i =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_icubhjh1i_getActionArgs,
    });
  const inst_evh_2434514588044777_success_hxsd8wvp3 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_icubhjh1i,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2434514588044777_request_fl9jk0g7b = new actionTypes.RequestAction({
    actionArgs: act_2434514588044777_request_fl9jk0g7b_getActionArgs,
    displayName: 'deleteService',
    events: [inst_evh_2434514588044777_success_hxsd8wvp3],
  });
  const inst_evh_2434514588044777_clickPreventStop_gpksvkfnm =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_request_fl9jk0g7b,
      event: 'click.prevent.stop',
      displayName: 'deleteService',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_gpksvkfnm],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_closeModal_of3hfm9fa =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_of3hfm9fa_getActionArgs,
    });
  const inst_evh_2167826738365259_success_2wio8nr8c =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_of3hfm9fa,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2167826738365259_request_kq4z07xo0 = new actionTypes.RequestAction({
    actionArgs: act_2167826738365259_request_kq4z07xo0_getActionArgs,
    displayName: 'deleteServiceFee',
    events: [inst_evh_2167826738365259_success_2wio8nr8c],
  });
  const inst_evh_2167826738365259_clickPreventStop_ucq5pb079 =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_request_kq4z07xo0,
      event: 'click.prevent.stop',
      displayName: 'deleteServiceFee',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_ucq5pb079],
      event: 'click.prevent.stop',
    });
  const act_3233739474466081_closeComponent_kq92lc9dg =
    new actionTypes.CloseComponentAction({});
  const inst_evh_3233739474466081_clickPreventStop_vcamqq83u =
    new actionTypes.EventHandlerImpl({
      action: act_3233739474466081_closeComponent_kq92lc9dg,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_3233739474466081_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3233739474466081_clickPreventStop_vcamqq83u],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_lk3ej7jf3,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_c1opecdov,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_7lopvbvbf,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_xdb0kjfut,
    inst_evh_1040167445267876_submit_ffe512i56,
    save: inst_evh_1040167445267876_submit_ffe512i56,
    inst_evh_1040167445267876_submit,
    inst_evh_3054425420812800_optionSelected_nvh3fup7q,
    inst_evh_3054425420812800_optionSelected_8gof7ik2d,
    inst_evh_3054425420812800_optionSelected_ca24tmnd2,
    inst_evh_3054425420812800_optionSelected,
    inst_evh_1740443260293186_success_xecrkyvi5,
    inst_evh_1740443260293186_clickPreventStop_dd8idry6u,
    lockServiceFee: inst_evh_1740443260293186_clickPreventStop_dd8idry6u,
    inst_evh_1740443260293186_clickPreventStop,
    inst_evh_2140198529698299_success_gji4f9yp7,
    inst_evh_2140198529698299_clickPreventStop_5f8hjcagm,
    lockService: inst_evh_2140198529698299_clickPreventStop_5f8hjcagm,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2434514588044777_success_hxsd8wvp3,
    inst_evh_2434514588044777_clickPreventStop_gpksvkfnm,
    deleteService: inst_evh_2434514588044777_clickPreventStop_gpksvkfnm,
    inst_evh_2434514588044777_clickPreventStop,
    inst_evh_2167826738365259_success_2wio8nr8c,
    inst_evh_2167826738365259_clickPreventStop_ucq5pb079,
    deleteServiceFee: inst_evh_2167826738365259_clickPreventStop_ucq5pb079,
    inst_evh_2167826738365259_clickPreventStop,
    inst_evh_3233739474466081_clickPreventStop_vcamqq83u,
    inst_evh_3233739474466081_clickPreventStop,
  };

  const evh_7315092382398562_close_lk3ej7jf3 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_lk3ej7jf3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_c1opecdov = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_c1opecdov.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_7lopvbvbf = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_7lopvbvbf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_xdb0kjfut = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_xdb0kjfut.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_ffe512i56 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_ffe512i56.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3054425420812800_optionSelected_nvh3fup7q = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3054425420812800_optionSelected_nvh3fup7q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3054425420812800_optionSelected_8gof7ik2d = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3054425420812800_optionSelected_8gof7ik2d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3054425420812800_optionSelected_ca24tmnd2 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3054425420812800_optionSelected_ca24tmnd2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3054425420812800_optionSelected = async (event, scope) => {
    return await actions.inst_evh_3054425420812800_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1740443260293186_success_xecrkyvi5 = async (event, scope) => {
    return await actions.inst_evh_1740443260293186_success_xecrkyvi5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1740443260293186_clickPreventStop_dd8idry6u = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1740443260293186_clickPreventStop_dd8idry6u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1740443260293186_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1740443260293186_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_gji4f9yp7 = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_gji4f9yp7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_5f8hjcagm = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_5f8hjcagm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_success_hxsd8wvp3 = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_success_hxsd8wvp3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_gpksvkfnm = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_gpksvkfnm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_success_2wio8nr8c = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_success_2wio8nr8c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_ucq5pb079 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_ucq5pb079.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3233739474466081_clickPreventStop_vcamqq83u = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3233739474466081_clickPreventStop_vcamqq83u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3233739474466081_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3233739474466081_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractServiceEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="contract"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ItpText strong :text="pgettext('ContractServiceEditor', 'Service')">
          </ItpText>
          <ItpFormGroup
            name="service"
            :label="pgettext('ContractService', 'Service')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Select the product or service the invoice line refers to.'
              )
            "
            required
          >
            <ItpSelect2
              name="client"
              v-model="item.service"
              :value="item.service"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.service =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              bind-object
              :options="services"
              textField="name"
              :readonly="isServiceReadonly"
              @optionSelected="evh_3054425420812800_optionSelected($event, {})"
            >
            </ItpSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            :content-cols-md="9"
            name="invoice_group_id"
            :label="pgettext('ContractService', 'Invoice Group')"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'The invoice group this service belongs to.'
              )
            "
          >
            <ItpFormSelect2
              name="invoice_group_id"
              v-model="item.invoice_group_id"
              :value="item.invoice_group_id"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.invoice_group_id =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :multiple="b_7307096920626688_multiple"
              :options="invoiceGroups"
              valueField="id"
              textField="name"
              clearable
              :readonly="isServiceReadonly"
            >
            </ItpFormSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            :label-cols-md="3"
            name="intermediated_service"
            :label="pgettext('ContractService', 'Intermediated service')"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Select this option if the service is provided via an intermediary party.'
              )
            "
          >
            <ItpFormCheckbox
              name="intermediated_service"
              v-model="item.intermediated_service"
              :value="item.intermediated_service"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.intermediated_service =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :readonly="isServiceReadonly"
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            name="valid_from"
            :label="pgettext('ContractService', 'Valid from')"
            :label-cols-md="3"
            :content-cols-md="4"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Specifies the start date of the service period.'
              )
            "
          >
            <ItpFormDatetime
              name="valid_from"
              v-model="item.valid_from"
              required
              :readonly="isServiceReadonly"
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            name="valid_to"
            :label="pgettext('ContractService', 'Valid to')"
            :label-cols-md="3"
            :content-cols-md="4"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'The service end date. Set only if it\'s different from the contract\'s end date.'
              )
            "
          >
            <ItpFormDatetime
              name="valid_to"
              v-model="item.valid_to"
              :readonly="isServiceReadonly"
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            name="description"
            :label="pgettext('ContractService', 'Description')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Internal notes for reference only. Not visible externally.'
              )
            "
          >
            <ItpFormTextarea
              name="description"
              v-model="item.description"
              :value="item.description"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.description =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :readonly="isServiceReadonly"
              :rows="5"
            >
            </ItpFormTextarea>
          </ItpFormGroup>
          <hr />
          <fieldset>
            <legend>
              {{ pgettext('ContractServiceEditor|Section', 'Invoicing') }}
            </legend>
            <ItpFormGroup
              name="name"
              :label="pgettext('ContractService', 'Name')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'This text will appear on the invice line.'
                )
              "
              required
            >
              <ItpFormInput
                name="name"
                v-model="item.name"
                :value="item.name"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.name =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                required
                :readonly="isServiceReadonly"
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              name="invoice_notes"
              :label="pgettext('ContractServiceFee', 'Invoice notes')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'Note to display on the invoice line.'
                )
              "
            >
              <ItpFormTextarea
                name="invoice_notes"
                v-model="item.invoice_notes"
                :value="item.invoice_notes"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.invoice_notes =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :readonly="isServiceReadonly"
              >
              </ItpFormTextarea>
            </ItpFormGroup>
            <ItpFormGroup
              name="billing_frequency"
              :label="pgettext('ContractService', 'Billing frequency')"
              :label-cols-md="3"
              :content-cols-md="3"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The cycle length of issuing the invoices.'
                )
              "
            >
              <BillingFrequencySelector
                v-model="item.billing_frequency"
                :value="item.billing_frequency"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.billing_frequency =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :readonly="isServiceReadonly"
              >
              </BillingFrequencySelector>
            </ItpFormGroup>
            <ItpFormGroup
              name="billing_date_rule_anchor"
              :label="pgettext('ContractService', 'Billing date rule')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The cycle length of issuing the invoices.'
                )
              "
            >
              <BillingDateRuleFields
                v-model="item.billing_date_rule"
                :value="item.billing_date_rule"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.billing_date_rule =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :readonly="isServiceReadonly"
              >
              </BillingDateRuleFields>
            </ItpFormGroup>
            <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
              <hr class="dots" />
            </ItpFormGroup>
            <ItpFormGroup
              name="due_date_rule_anchor"
              :label="pgettext('ContractService', 'Due date rule')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The rule of calculating the payment dates.'
                )
              "
            >
              <DueDateRuleFields
                v-model="item.due_date_rule"
                :value="item.due_date_rule"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.due_date_rule =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :readonly="isServiceReadonly"
              >
              </DueDateRuleFields>
            </ItpFormGroup>
            <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
              <hr class="dots" />
            </ItpFormGroup>
            <ItpFormGroup
              name="billing_currency"
              :label="pgettext('ContractService', 'Billing currency')"
              :label-cols-md="3"
              :content-cols-md="3"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The currency to use on the invoice.'
                )
              "
            >
              <BillingCurrencySelector
                v-model="item.billing_currency"
                :value="item.billing_currency"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.billing_currency =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :readonly="isServiceReadonly"
              >
              </BillingCurrencySelector>
            </ItpFormGroup>
            <ItpFormGroup
              name="exchange_rate_source"
              :label="pgettext('ContractService', 'Exchange rate source')"
              :label-cols-md="3"
              :content-cols-md="3"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The currency exchange rate\'s provider.'
                )
              "
            >
              <ExchangeRateSourceSelector
                v-model="item.exchange_rate_source"
                :value="item.exchange_rate_source"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.exchange_rate_source =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :readonly="isServiceReadonly"
              >
              </ExchangeRateSourceSelector>
            </ItpFormGroup>
            <ItpFormGroup
              name="exchange_rate_date_rule"
              :label="pgettext('ContractService', 'Exchange rate date rule')"
              :label-cols-md="3"
              :content-cols-md="9"
              :labelAlign="labelAlign"
              :helpTooltip="
                pgettext(
                  'ContractService|Tooltip',
                  'The date used to determine the exchange rate for billing purposes.'
                )
              "
            >
              <ExchangeRateDateRuleFields
                v-model="item.exchange_rate_date_rule"
                :value="item.exchange_rate_date_rule"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (item.exchange_rate_date_rule =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :readonly="isServiceReadonly"
              >
              </ExchangeRateDateRuleFields>
            </ItpFormGroup>
          </fieldset>
          <hr v-if="displayFee" />
          <ItpText
            strong
            :text="pgettext('ContractServiceEditor', 'Fee')"
            v-if="displayFee"
          >
          </ItpText>
          <ItpFormGroup
            name="fee_valid_from"
            :label="pgettext('ContractServiceFee', 'Valid from')"
            :label-cols-md="3"
            :content-cols-md="3"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext('ContractService|Tooltip', 'The start date of the fee.')
            "
            v-if="displayFeeDates"
          >
            <ItpFormDatetime
              name="fee_valid_from"
              v-model="feeItem.valid_from"
              required
            >
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            name="fee_valid_to"
            :label="pgettext('ContractServiceFee', 'Valid to')"
            :label-cols-md="3"
            :content-cols-md="3"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'The end date of the fee. Set only if it\'s different from the service\'s end date.'
              )
            "
            v-if="displayFeeDates"
          >
            <ItpFormDatetime name="fee_valid_to" v-model="feeItem.valid_to">
            </ItpFormDatetime>
          </ItpFormGroup>
          <ItpFormGroup
            name="fee_amount"
            :label="pgettext('ContractServiceFee', 'Amount')"
            :label-cols-md="3"
            :content-cols-md="5"
            :labelAlign="labelAlign"
            contentClass="input-group"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'The price per unit of the fee.'
              )
            "
            v-if="displayFee"
          >
            <ItpFormInput
              type="number"
              name="fee_amount"
              v-model="feeItem.amount"
              :value="feeItem.amount"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (feeItem.amount =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              required
              class="col-9"
            >
            </ItpFormInput>
            <ItpFormSelect2
              name="currency"
              v-model="feeItem.currency"
              :value="feeItem.currency"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (feeItem.currency =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              :options="currencies"
              textField="code"
              valueField="code"
              required
              class="col-3"
            >
            </ItpFormSelect2>
          </ItpFormGroup>
          <ItpFormGroup
            name="fee_unit"
            :label="pgettext('ContractServiceFee', 'Invoice unit')"
            :label-cols-md="3"
            :content-cols-md="2"
            :labelAlign="labelAlign"
            contentClass="input-group"
            :helpTooltip="
              pgettext('ContractService|Tooltip', 'The unit used for billing.')
            "
            v-if="displayFee"
          >
            <ItpFormInput
              name="fee_unit"
              v-model="feeItem.unit"
              :value="feeItem.unit"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (feeItem.unit =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              required
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="fee_quantity"
            :label="pgettext('ContractServiceFee', 'Quantity')"
            :label-cols-md="3"
            :content-cols-md="2"
            :labelAlign="labelAlign"
            contentClass="input-group"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'The quantity to bill for each period.'
              )
            "
            v-if="displayFee"
          >
            <ItpFormInput
              type="number"
              name="fee_quantity"
              v-model="feeItem.quantity"
              :value="feeItem.quantity"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (feeItem.quantity =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="fee_unit_of_quantity"
            :label="pgettext('ContractServiceFee', 'Unit of quantity')"
            :label-cols-md="3"
            :content-cols-md="2"
            :labelAlign="labelAlign"
            contentClass="input-group"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'The unit of quantity - if different from the billing quantity.'
              )
            "
            v-if="displayFee"
          >
            <ItpFormInput
              name="fee_unit_of_quantity"
              v-model="feeItem.unit_of_quantity"
              :value="feeItem.unit_of_quantity"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (feeItem.unit_of_quantity =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            name="fee_inflation_indexed"
            :label="pgettext('ContractServiceFee', 'Inflation indexed')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Indicates if the unit price should be automatically adjusted based on indexation rules.'
              )
            "
            v-if="displayFee"
          >
            <ItpFormCheckbox
              name="fee_inflation_indexed"
              v-model="feeItem.inflation_indexed"
              :value="feeItem.inflation_indexed"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (feeItem.inflation_indexed =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </ItpFormGroup>
          <ItpFormGroup
            name="fee_notes"
            :label="pgettext('ContractServiceFee', 'Notes')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            :helpTooltip="
              pgettext(
                'ContractService|Tooltip',
                'Notes for internal use only.'
              )
            "
            v-if="displayFee"
          >
            <ItpFormTextarea
              name="fee_notes"
              v-model="feeItem.notes"
              :value="feeItem.notes"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (feeItem.notes =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormTextarea>
          </ItpFormGroup>
          <ItpButton
            :text="pgettext('Button', 'Lock Service Fee')"
            variant="light"
            icon="fa-lock"
            :spinning="actions.lockServiceFee.isRunning"
            v-if="mode === 'editServiceFee' && !feeItem.locked"
            @click.prevent.stop="
              evh_1740443260293186_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Lock Service')"
          variant="light"
          icon="fa-lock"
          :spinning="actions.lockService.isRunning"
          v-if="mode === 'editService' && !service.locked"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Delete Service')"
          variant="danger"
          icon="fa-times"
          :spinning="actions.deleteService.isRunning"
          class="me-auto"
          v-if="mode === 'editService' && isDeletable"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Delete Fee')"
          variant="danger"
          icon="fa-times"
          :spinning="actions.deleteServiceFee.isRunning"
          class="me-auto"
          v-if="mode === 'editServiceFee' && isDeletable"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.contract || !forms.contract.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
          v-if="isEditable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_3233739474466081_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
